import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  LinearProgress,
  Link as MuiLink,
  Tooltip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as FileIcon,
  InsertDriveFile as DocumentIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { ServicePackageFormData } from '../../types/servicePackage'
import { reportTypes, exportFormats } from '../../data/servicePackageMockData'
import { getFieldError } from '../../utils/servicePackageValidation'
import { uploadContractDocuments } from '../../services/servicePackageService'

interface Section5DocumentationProps {
  formData: ServicePackageFormData
  onUpdate: (data: Partial<ServicePackageFormData>) => void
  errors: any
}

const MAX_DOCUMENTS = 5

export const Section5Documentation: React.FC<Section5DocumentationProps> = ({
  formData,
  onUpdate,
  errors,
}) => {
  const theme = useTheme()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleReportChange = (reportId: string, checked: boolean) => {
    const updatedReports = checked
      ? [...formData.documentation.reportsRequired, reportId]
      : formData.documentation.reportsRequired.filter(id => id !== reportId)

    onUpdate({
      documentation: {
        ...formData.documentation,
        reportsRequired: updatedReports,
      },
    })
  }

  const handleExportFormatChange = (formatId: string, checked: boolean) => {
    const updatedFormats = checked
      ? [...formData.documentation.exportFormats, formatId]
      : formData.documentation.exportFormats.filter(id => id !== formatId)

    onUpdate({
      documentation: {
        ...formData.documentation,
        exportFormats: updatedFormats,
      },
    })
  }

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) {
      return
    }

    const selectedFiles = Array.from(files)
    const currentCount = formData.documentation.contractAttachments.length
    const remainingSlots = MAX_DOCUMENTS - currentCount

    // Check if adding these files would exceed the limit
    if (selectedFiles.length > remainingSlots) {
      setUploadError(
        `You can only upload ${remainingSlots} more document${
          remainingSlots === 1 ? '' : 's'
        }. Maximum ${MAX_DOCUMENTS} documents allowed.`
      )
      if (event.target) {
        event.target.value = ''
      }
      return
    }

    try {
      setUploadError(null)
      setIsUploading(true)
      const uploadedFiles = await uploadContractDocuments(selectedFiles)

      if (!uploadedFiles.length) {
        throw new Error('No files were uploaded. Please try again.')
      }

      const newAttachments = uploadedFiles.map((uploaded, index) => {
        const originalFile = selectedFiles[index]
        return {
          id: `${Date.now()}-${uploaded.fileName || originalFile?.name || index}`,
          name:
            originalFile?.name ||
            uploaded.fileName ||
            `Attachment-${index + 1}`,
          size: originalFile?.size ?? 0,
          type: originalFile?.type ?? '',
          url: uploaded.signedUrl || uploaded.location || '',
        }
      })

      onUpdate({
        documentation: {
          ...formData.documentation,
          contractAttachments: [
            ...formData.documentation.contractAttachments,
            ...newAttachments,
          ],
        },
      })
    } catch (error) {
      console.error('Error uploading contract documents:', error)
      setUploadError(
        error instanceof Error
          ? error.message
          : 'Failed to upload files. Please try again.'
      )
    } finally {
      setIsUploading(false)
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  const removeAttachment = (attachmentId: string) => {
    const updatedAttachments =
      formData.documentation.contractAttachments.filter(
        attachment => attachment.id !== attachmentId
      )

    onUpdate({
      documentation: {
        ...formData.documentation,
        contractAttachments: updatedAttachments,
      },
    })
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getAttachmentDisplayName = (
    attachment: (typeof formData.documentation.contractAttachments)[number]
  ) => {
    // If we have a name, use it
    if (attachment.name) return attachment.name

    // If we have a URL, extract filename from it
    if (attachment.url) {
      try {
        // Remove query parameters first
        const urlWithoutQuery = attachment.url.split('?')[0]

        // Try to parse as URL first
        try {
          const urlObj = new URL(urlWithoutQuery)
          const pathname = urlObj.pathname
          const segments = pathname.split('/').filter(Boolean)
          const filename = segments[segments.length - 1] || 'Document'
          return decodeURIComponent(filename)
        } catch {
          // If URL parsing fails, try direct path extraction
          const segments = urlWithoutQuery.split('/').filter(Boolean)
          const filename = segments[segments.length - 1] || 'Document'
          return decodeURIComponent(filename)
        }
      } catch {
        return 'Document'
      }
    }
    return 'Document'
  }

  const getAttachmentType = (
    attachment: (typeof formData.documentation.contractAttachments)[number]
  ) => {
    if (attachment.type) return attachment.type

    const name = attachment.name || attachment.url || ''
    const extension = name.split('.').pop()?.toLowerCase()

    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp':
        return `image/${extension === 'jpg' ? 'jpeg' : extension}`
      case 'pdf':
        return 'application/pdf'
      case 'doc':
      case 'docx':
        return 'application/msword'
      case 'txt':
        return 'text/plain'
      default:
        return ''
    }
  }

  const renderAttachmentPreview = (
    attachment: (typeof formData.documentation.contractAttachments)[number]
  ) => {
    const type = getAttachmentType(attachment)
    const isImage = type.startsWith('image/')

    if (isImage && attachment.url) {
      return (
        <Box
          component="img"
          src={attachment.url}
          alt={getAttachmentDisplayName(attachment)}
          sx={{
            width: 64,
            height: 64,
            objectFit: 'cover',
            borderRadius: 1,
            border: theme => `1px solid ${theme.palette.divider}`,
            mr: 2,
          }}
        />
      )
    }

    return <FileIcon sx={{ mr: 2, color: 'primary.main' }} />
  }

  const reportsError = getFieldError(errors, 'reportsRequired')
  const exportFormatsError = getFieldError(errors, 'exportFormats')

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Documentation & Deliverables
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure required reports, export formats, and contract attachments.
      </Typography>

      {/* Required Reports */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Required Reports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the types of reports and documentation required for this
            service package.
          </Typography>

          {reportsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reportsError}
            </Alert>
          )}

          <FormGroup>
            <Grid container spacing={1}>
              {reportTypes.map(report => (
                <Grid item xs={12} sm={6} md={4} key={report.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.documentation.reportsRequired.includes(
                          report.id
                        )}
                        onChange={e =>
                          handleReportChange(report.id, e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {report.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {report.description}
                        </Typography>
                        {report.required && (
                          <Chip
                            label="Required"
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>

          {/* Selected Reports Summary */}
          {formData.documentation.reportsRequired.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Reports (
                {formData.documentation.reportsRequired.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.documentation.reportsRequired
                  .map(id => reportTypes.find(report => report.id === id))
                  .filter((report): report is NonNullable<typeof report> =>
                    report != null
                  )
                  .map(report => (
                    <Chip
                      key={report.id}
                      label={report.name}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Export Formats */}
      {/* <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Export Formats
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the file formats available for exporting reports and
            documentation.
          </Typography>

          {exportFormatsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {exportFormatsError}
            </Alert>
          )}

          <FormGroup>
            <Grid container spacing={1}>
              {exportFormats.map(format => (
                <Grid item xs={12} sm={6} md={4} key={format.id}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.documentation.exportFormats.includes(
                          format.id
                        )}
                        onChange={e =>
                          handleExportFormatChange(format.id, e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {format.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {format.extension} - {format.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </FormGroup>

          {formData.documentation.exportFormats.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Export Formats (
                {formData.documentation.exportFormats.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.documentation.exportFormats.map(formatId => {
                  const format = exportFormats.find(f => f.id === formatId)
                  return format ? (
                    <Chip
                      key={format.id}
                      label={`${format.name} (${format.extension})`}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  ) : null
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card> */}

      {/* Contract Attachments */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upload any templates, consent forms, or other documentation files
            that patients will need to review and sign.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>
              You can upload up to {MAX_DOCUMENTS} documents only.
            </strong>{' '}
            ({formData.documentation.contractAttachments.length}/{MAX_DOCUMENTS}{' '}
            uploaded)
          </Typography>

          {/* File Upload Area */}
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              border: '2px dashed',
              borderColor:
                formData.documentation.contractAttachments.length >=
                MAX_DOCUMENTS
                  ? 'grey.300'
                  : 'grey.300',
              bgcolor:
                formData.documentation.contractAttachments.length >=
                MAX_DOCUMENTS
                  ? 'action.disabledBackground'
                  : 'transparent',
              '&:hover': {
                borderColor:
                  formData.documentation.contractAttachments.length >=
                  MAX_DOCUMENTS
                    ? 'grey.300'
                    : 'primary.main',
                bgcolor:
                  formData.documentation.contractAttachments.length >=
                  MAX_DOCUMENTS
                    ? 'action.disabledBackground'
                    : 'action.hover',
              },
              cursor:
                formData.documentation.contractAttachments.length >=
                MAX_DOCUMENTS
                  ? 'not-allowed'
                  : 'pointer',
              mb: 2,
              opacity:
                formData.documentation.contractAttachments.length >=
                MAX_DOCUMENTS
                  ? 0.6
                  : 1,
            }}
            onClick={() => {
              if (
                formData.documentation.contractAttachments.length <
                MAX_DOCUMENTS
              ) {
                fileInputRef.current?.click()
              }
            }}
          >
            <UploadIcon
              sx={{
                fontSize: 48,
                color:
                  formData.documentation.contractAttachments.length >=
                  MAX_DOCUMENTS
                    ? 'grey.400'
                    : 'grey.400',
                mb: 1,
              }}
            />
            <Typography variant="h6" gutterBottom>
              {formData.documentation.contractAttachments.length >=
              MAX_DOCUMENTS
                ? 'Maximum Documents Reached'
                : 'Upload Documents'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formData.documentation.contractAttachments.length >=
              MAX_DOCUMENTS
                ? `You have reached the maximum limit of ${MAX_DOCUMENTS} documents. Please remove a document to upload a new one.`
                : 'Click to select files or drag and drop'}
            </Typography>
            {formData.documentation.contractAttachments.length <
              MAX_DOCUMENTS && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG (Max 10MB per
                file)
              </Typography>
            )}
            {isUploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mt: 1 }}
                >
                  Uploading files...
                </Typography>
              </Box>
            )}
          </Paper>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.txt,.jpg,.png"
            onChange={handleFileUpload}
            disabled={
              formData.documentation.contractAttachments.length >= MAX_DOCUMENTS
            }
            style={{ display: 'none' }}
          />

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {/* Uploaded Files List */}
          {formData.documentation.contractAttachments.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Uploaded Files (
                {formData.documentation.contractAttachments.length}):
              </Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {formData.documentation.contractAttachments.map(attachment => {
                  const fileName = getAttachmentDisplayName(attachment)
                  const fileUrl = attachment.url
                  const sizeText = attachment.size
                    ? formatFileSize(attachment.size)
                    : null
                  const type = getAttachmentType(attachment)
                  const typeText = type ? type.toUpperCase() : null
                  const secondaryText = [sizeText, typeText]
                    .filter(Boolean)
                    .join(' • ')

                  return (
                    <Paper
                      key={attachment.id}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: alpha(theme.palette.background.paper, 0.6),
                        borderColor: alpha(theme.palette.secondary.main, 0.2),
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.secondary.main, 0.08),
                          borderColor: alpha(theme.palette.secondary.main, 0.4),
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${alpha(
                            theme.palette.secondary.main,
                            0.15
                          )}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: 1,
                            bgcolor: alpha(theme.palette.secondary.main, 0.1),
                            color: 'secondary.main',
                          }}
                        >
                          <DocumentIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {fileUrl ? (
                            <MuiLink
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="none"
                              sx={{
                                display: 'block',
                                fontWeight: 500,
                                color: 'text.primary',
                                fontSize: '0.875rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  color: 'secondary.main',
                                  textDecoration: 'underline',
                                },
                              }}
                              title={fileName}
                            >
                              {fileName}
                            </MuiLink>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={fileName}
                            >
                              {fileName}
                            </Typography>
                          )}
                          {secondaryText && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {secondaryText}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {fileUrl && (
                            <IconButton
                              size="small"
                              component="a"
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: 'text.secondary',
                                '&:hover': {
                                  color: 'secondary.main',
                                  bgcolor: alpha(
                                    theme.palette.secondary.main,
                                    0.1
                                  ),
                                },
                              }}
                            >
                              <OpenInNewIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => removeAttachment(attachment.id)}
                            color="error"
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.error.main, 0.1),
                              },
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Paper>
                  )
                })}
              </Stack>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Documents:</strong> These files will be presented to
              patients for review and electronic signature before booking
              sessions. Ensure all legal documents are up-to-date and compliant
              with local regulations.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </Box>
  )
}

export default Section5Documentation
